/**
 * Generator: Single page — (authenticated)/<n>/page.tsx
 * Server component that fetches the singleton record and renders the form.
 */

import type { GeneratedFile, GeneratorOptions, Schema } from '@core-engine/schema/types.js'
import { createGeneratedFile } from '@core-engine/utils/generated-files.js'
import { singularize, toPascalCase } from '@core-engine/utils/string.js'
import { resolveSchemaActionImportPath } from '@next-adapter/config/paths.js'
import { buildSingleDevModeIntegration } from '@next-adapter/generators/dev-mode-integration.js'

// ============================================================================
// Public API
// ============================================================================

export interface SinglePageGeneratorResult {
  files: GeneratedFile[]
}

/**
 * Generate server page for a single (singleton) schema.
 * Calls getXxx() and renders XxxForm with initialData (null on first visit).
 */
export function generateSinglePage(
  schema: Schema,
  pagesDir: string,
  options: GeneratorOptions = {}
): SinglePageGeneratorResult {
  const singular = singularize(schema.name)
  const Singular = toPascalCase(singular)
  const PageName = toPascalCase(schema.name)
  const actionImportPath = resolveSchemaActionImportPath(schema.name, options.outputNamespace)
  const adminRoutePath = options.adminRoutePath ?? '/admin'
  const devModeIntegration = buildSingleDevModeIntegration(
    schema,
    options.adminDir ?? 'admin',
    actionImportPath,
    options.adminImportAlias
  )
  const settingsImport =
    schema.name === 'settings' ? '' : "import { getSetting } from '@admin/actions/settings'\n"
  const settingsAuditImports =
    schema.name === 'settings'
      ? "import { Button } from '@admin/components/ui/button'\nimport { ScrollText } from 'lucide-react'\nimport Link from 'next/link'\n"
      : ''
  const dataLoad =
    schema.name === 'settings'
      ? `  const data = await get${Singular}()
`
      : `  const [data, initialSettings] = await Promise.all([
    get${Singular}(),
    getSetting()
  ])
`
  const initialSettingsProp =
    schema.name === 'settings' ? 'initialSettings={data}' : 'initialSettings={initialSettings}'
  const actionsContent =
    schema.name === 'settings'
      ? `<div className="flex items-center gap-2">
            <Button asChild variant="outline" size="sm">
              <Link href="${adminRoutePath}/system/audit-log">
                <ScrollText />
                Audit Log
              </Link>
            </Button>
            <${devModeIntegration.buttonExportName} ${initialSettingsProp} />
          </div>`
      : `<${devModeIntegration.buttonExportName} ${initialSettingsProp} />`

  const content = `${settingsImport}${settingsAuditImports}import { get${Singular} } from '@admin/actions/${actionImportPath}'
import { PageHeader } from '@admin/components/shared/page-header'
import { ${devModeIntegration.buttonExportName} } from '${devModeIntegration.buttonImportPath}'
import { ${Singular}Form } from './${schema.name}-form'
import React from 'react'

export default async function ${PageName}Page() {
${dataLoad}

  return (
    <React.Fragment>
      <PageHeader
        title="${schema.label}"
        actions={${actionsContent}}
      />
      <main className="container mx-auto max-w-5xl w-full p-4">
        <${Singular}Form initialData={data} />
      </main>
    </React.Fragment>
  )
}
`

  return {
    files: [
      createGeneratedFile(`${pagesDir}/${schema.name}/page.tsx`, content),
      ...devModeIntegration.files
    ]
  }
}
